<template>
  <div class="tabs">
    <div class="tabs__slider-wrapper">
      <span ref="leftInd" class="tabs__left-ind"></span>
      <span ref="rightInd" class="tabs__right-ind"></span>
      <div ref="tabsNavSlider" class="tabs__nav-slider">
        <ul ref="tabsHeader" class="tabs__header">
          <li
            class="tabs__tab-item"
            v-for="(tab, index) in tabs"
            :key="tab.title"
            :class="{ _selected: index == selectedIndex }"
            ref="tabsNavItem"
          >
            <div
              @click="selectTab(index)"
              role="button"
              class="tabs__tab-label"
              v-if="tabLabelType"
              v-html="getTabLabel(tab.title)"
            ></div>
            <div
              @click="selectTab(index)"
              role="button"
              class="tabs__tab-label"
              v-else
            >
              <span class="tabs__tab-label-text" v-html="tab.title"></span>
            </div>
          </li>
        </ul>
      </div>
    </div>
    <div class="tabs__content">
      <slot />
    </div>
  </div>
</template>

<script>
export default {
  name: "g-tabs",
  props: {
    tabLabelType: String,
  },
  data: () => ({
    selectedIndex: 0,
    tabs: [],
    tabsWidth: 0,
    tabsSliderWidth: 0,
    leftPos: 0,
  }),
  methods: {
    selectTab(i) {
      let that = this;
      this.selectedIndex = i;
      this.tabs.forEach((tab, index) => {
        tab.isActive = index === i;
        tab.selectedIndex = that.selectedIndex;
      });
    },
    getTabLabel(title) {
      return (
        "<" +
        this.tabLabelType +
        " class='tabs__tab-label-text'>" +
        title +
        "</" +
        this.tabLabelType +
        ">"
      );
    },
    mouseScroll() {
      const slider = this.$refs.tabsNavSlider;
      let that = this;
      let isDown = false;
      let startX;
      let scrollLeft;

      slider.addEventListener(
        "mousedown",
        (e) => {
          isDown = true;
          startX = e.pageX - slider.offsetLeft;
          scrollLeft = slider.scrollLeft;
        },
        { passive: true }
      );
      slider.addEventListener(
        "mouseleave",
        () => {
          isDown = false;
        },
        { passive: true }
      );
      slider.addEventListener(
        "mouseup",
        () => {
          isDown = false;
        },
        { passive: true }
      );
      slider.addEventListener("mousemove", (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - slider.offsetLeft;
        const walk = x - startX;
        slider.scrollLeft = scrollLeft - walk;
        that.setInds();
      });
    },
    touchScroll() {
      const slider = this.$refs.tabsNavSlider;
      let that = this;
      let detectScrollEnded = function () {
        let currentScroll = slider.scrollLeft;

        function testIfScrolling() {
          if (slider.scrollLeft !== currentScroll) {
            window.requestAnimationFrame(testIfScrolling);
            that.setInds();
          } else {
            that.setInds();
          }
        }
        window.requestAnimationFrame(testIfScrolling);
      };
      slider.addEventListener("touchend", detectScrollEnded, { passive: true });
      slider.addEventListener(
        "touchmove",
        () => {
          that.setInds();
        },
        { passive: true }
      );
    },
    calculateTabSliderWidth() {
      this.tabsSliderWidth = this.$refs.tabsNavSlider.clientWidth;
    },
    getNavWidth() {
      const item = this.$refs.tabsNavItem;
      let itemsWidthSum = 0;

      for (let i = 0; i < this.tabs.length; i++) {
        itemsWidthSum += parseInt(item[i].clientWidth);
      }
      this.tabsWidth = itemsWidthSum;
      this.setInds();
    },
    setInds() {
      const slider = this.$refs.tabsNavSlider;
      const leftInd = this.$refs.leftInd;
      const rightInd = this.$refs.rightInd;
      const visibleClass = "_visible";
      this.leftPos = slider.scrollLeft;

      if (this.leftPos != undefined) {
        if (
          this.tabsSliderWidth < this.tabsWidth &&
          this.leftPos < this.tabsWidth - this.tabsSliderWidth
        ) {
          rightInd.classList.add(visibleClass);
        } else {
          rightInd.classList.remove(visibleClass);
        }

        if (this.leftPos > 0) {
          leftInd.classList.add(visibleClass);
        } else {
          leftInd.classList.remove(visibleClass);
        }
      }
    },
  },
  created() {
    this.tabs = this.$children;
    window.addEventListener("resize", this.calculateTabSliderWidth, {
      passive: true,
    });
  },
  destroyed() {
    window.removeEventListener("resize", this.calculateTabSliderWidth, {
      passive: true,
    });
  },
  updated() {
    this.getNavWidth();
  },
  mounted() {
    this.selectTab(0);
    this.$nextTick(() => {
      this.calculateTabSliderWidth();
      this.mouseScroll();
      this.touchScroll();
    });
  },
  watch: {
    tabs: function () {
      this.selectTab(0);
    },
    tabsSliderWidth: function () {
      this.setInds();
    },
  },
};
</script>

<style lang="scss" scoped>
.tabs {
  $self: &;
  border-bottom: 1px solid $border-color;

  &__slider-wrapper {
    margin-bottom: 28px;
    position: relative;
  }

  &__left-ind,
  &__right-ind {
    background-color: $blue-dark;
    border: 2px solid #fff;
    border-radius: 50%;
    opacity: 0;
    pointer-events: none;
    position: absolute;
    top: 26%;
    height: 22px;
    width: 22px;
    z-index: 2;
    transition: opacity 0.15s ease-in-out;

    &._visible {
      opacity: 1;
    }

    &::after {
      content: "";
      display: block;
      position: absolute;
      top: 6px;
      width: 6px;
      height: 6px;
      border-top: 1px solid #fff;
      border-left: 1px solid #fff;
    }
  }

  &__left-ind {
    left: -10px;

    &::after {
      left: 7px;
      transform: rotate(-45deg);
    }
  }

  &__right-ind {
    right: -10px;

    &::after {
      right: 7px;
      transform: rotate(135deg);
    }
  }

  &__nav-slider {
    overflow-x: auto;
    overflow: -moz-scrollbars-none;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__header {
    display: flex;
    list-style: none;
    margin: 0;
    padding: 0;
    position: relative;
  }

  &__tab-item {
    display: inline-block;
    padding: 0 8px;
    position: relative;
    white-space: nowrap;
    user-select: none;

    &:first-of-type {
      margin-left: auto;
      padding-left: 0;
    }

    &:last-of-type {
      margin-right: auto;
      padding-right: 0;
    }
  }

  &__tab-label {
    cursor: pointer;
    border-radius: 3px;
    border: 1px solid $gray-20;
    margin-bottom: 12px;
    padding: 18px 24px;
    position: relative;

    &::after {
      background-color: $blue-dark;
      border-radius: 2px;
      border-right: 4px solid $blue-dark;
      border-bottom: 4px solid $blue-dark;
      content: "";
      display: none;
      height: 18px;
      width: 18px;
      margin: 0 auto;
      position: absolute;
      bottom: -6px;
      left: 0;
      right: 0;
      transform: rotate(45deg);
    }
  }

  #{ $self }__tab-item._selected {
    #{ $self }__tab-label {
      background-color: $blue-dark;
      border-color: $blue-dark;
      color: #fff;

      &::after {
        display: block;
      }
    }
  }

  #{ $self }__tab-item:not(&._selected) {
    #{ $self }__tab-label {
      @media (pointer: fine) {
        &:hover {
          @include shadow();
        }
      }
    }
  }

  &__content {
    padding: 32px 0;
    position: relative;

    &::before {
      background-color: $blue-dark;
      content: "";
      display: block;
      position: absolute;
      top: -3px;
      right: -1px;
      left: -1px;
      height: 2px;
    }
  }
}

::v-deep .tabs {
  &__tab-label-text {
    @include font-m();
    font-size: 16px;
    margin-bottom: 0;
  }
}
</style>
